// Reports > "PDF": the A4 Payment Report of OSA's own build (Reports/
// Reporthome.jsx generatePDF), ported for practiceFeaturesB6().paymentReportPdf.
// Built in the browser: jspdf + jspdf-autotable load only when the button is
// clicked (lazy import(), so neither is in the main bundle).
//
// Same content as OSA's: a banner, four summary cards (period, record count,
// grand total, cash / card), one row per payment, column totals on the last
// page and a footer on every page. Only the name is the practice's own
// (practiceShortName(): "OSA Payment Report") instead of a hard-coded "OSA".
//
// As in OSA's build (owner question open): the totals leave out the test
// patients in `excludedPatientIds`, while "Total Records" counts every row.
import dayjs from "dayjs";

const money = (v) => `$${(parseFloat(v) || 0).toFixed(2)}`;
const sum = (rows, key) => rows.reduce((a, x) => a + (parseFloat(x[key]) || 0), 0);

// The table body, one row per payment (exported for the tests).
export function paymentReportRows(data) {
  return data.map((item) => [
    item.patient_name || "",
    item.payment_date ? dayjs(item.payment_date).format("MMM D, YYYY\nh:mm A") : "",
    item.payment_location || "",
    item.copay ? money(item.copay) : "-",
    item.selfpay ? money(item.selfpay) : "-",
    item.payment_on_account ? money(item.payment_on_account) : "-",
    money(item.amount),
    item.type
      ? `${item.type}${item.ccnumber && item.ccnumber !== "0000" && item.ccnumber !== 0 ? `\n****${String(item.ccnumber).slice(-4)}` : ""}`
      : "-",
    item.status === 2 ? "Athena\nPosted" : "Self Pay",
  ]);
}

export function paymentReportFileName(startDate) {
  return `payment-report-${dayjs(startDate).format("YYYY-MM-DD")}.pdf`;
}

/**
 * Build the PDF (without saving it; generatePaymentReportPdf saves).
 * @param {object} p
 * @param {Array}  p.data          the rows on screen (Reporthome `data`)
 * @param {Date}   p.startDate     selectedDateRange.startDate
 * @param {Date}   p.endDate       selectedDateRange.endDate
 * @param {number} p.cashAmount    the toolbar's cash total
 * @param {number} p.cardAmount    the toolbar's card total (red chip)
 * @param {Array}  p.excludedPatientIds  test patients left out of the totals
 * @param {string} p.practiceName  practiceShortName()
 * @returns {{ doc, fileName }} the jsPDF document and its download name
 */
export async function buildPaymentReportPdf({
  data, startDate, endDate, cashAmount, cardAmount, excludedPatientIds = [], practiceName,
}) {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;

  const title = `${practiceName} Payment Report`;
  const startLabel = dayjs(startDate).format("MM/DD/YYYY");
  const endLabel = dayjs(endDate).format("MM/DD/YYYY");
  const dateLabel = startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;

  const forTotals = data.filter((item) => !excludedPatientIds.includes(Number(item.patientid)));
  const totalAmount = sum(forTotals, "amount");
  const totalCopay = sum(forTotals, "copay");
  const totalSelfpay = sum(forTotals, "selfpay");
  const totalPoa = sum(forTotals, "payment_on_account");

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  if (typeof doc.setProperties === "function") doc.setProperties({ title });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 32;

  // Top banner
  doc.setFillColor(80, 134, 154);
  doc.rect(0, 0, pageW, 52, "F");
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(title, margin, 33);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Generated: ${dayjs().format("MM/DD/YYYY  h:mm A")}`, pageW - margin, 33, { align: "right" });

  // Summary cards
  const cardY = 62;
  const cardH = 44;
  const cardW = (pageW - margin * 2 - 12) / 4;
  const summaryCards = [
    { label: "Report Period", value: dateLabel, bg: [240, 248, 252], accent: [80, 134, 154] },
    { label: "Total Records", value: `${data.length}`, bg: [240, 248, 252], accent: [80, 134, 154] },
    { label: "Grand Total", value: money(totalAmount), bg: [254, 242, 242], accent: [220, 80, 80] },
    { label: "Cash / Card", value: `${money(cashAmount)} / ${money(cardAmount)}`, bg: [240, 253, 244], accent: [34, 150, 83] },
  ];
  summaryCards.forEach((card, i) => {
    const cx = margin + i * (cardW + 4);
    doc.setFillColor(...card.bg);
    doc.roundedRect(cx, cardY, cardW, cardH, 4, 4, "F");
    doc.setDrawColor(...card.accent);
    doc.setLineWidth(1.5);
    doc.line(cx, cardY + 2, cx, cardY + cardH - 2); // left accent bar
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    doc.text(card.label.toUpperCase(), cx + 8, cardY + 14);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(30);
    doc.text(card.value, cx + 8, cardY + 32);
  });

  // Table
  autoTable(doc, {
    startY: cardY + cardH + 14,
    margin: { left: margin, right: margin },
    head: [["Patient Name", "Paid Date", "Location", "Co-Pay", "Self-Pay", "Pmt on Acct", "Amount", "Method", "Status"]],
    body: paymentReportRows(data),
    foot: [["", "", "", money(totalCopay), money(totalSelfpay), money(totalPoa), money(totalAmount), "", ""]],
    showFoot: "lastPage",
    styles: {
      fontSize: 8,
      cellPadding: { top: 5, right: 5, bottom: 5, left: 5 },
      valign: "middle",
      lineColor: [220, 220, 220],
      lineWidth: 0.3,
      textColor: [40, 40, 40],
    },
    headStyles: {
      fillColor: [80, 134, 154],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: { top: 7, right: 5, bottom: 7, left: 5 },
    },
    footStyles: {
      fillColor: [235, 245, 250],
      textColor: [20, 20, 20],
      fontStyle: "bold",
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    // OSA's widths summed to 559pt on a 531pt line (A4 less the margins), so
    // the table ran 28pt into the right margin. The name column now takes
    // what the others leave (about 88pt).
    columnStyles: {
      0: { cellWidth: "auto", fontStyle: "bold" },
      1: { cellWidth: 72, halign: "center" },
      2: { cellWidth: 72 },
      3: { cellWidth: 42, halign: "right" },
      4: { cellWidth: 42, halign: "right" },
      5: { cellWidth: 52, halign: "right" },
      6: { cellWidth: 52, halign: "right", fontStyle: "bold", textColor: [30, 100, 60] },
      7: { cellWidth: 66 },
      8: { cellWidth: 45, halign: "center" },
    },
  });

  // Footer on every page, drawn once the table is laid out so "of N" is the
  // real page count (OSA's build drew it per page as the table grew, which
  // printed "Page 1 of 1" on page 1 of a longer report).
  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(160);
    doc.text(
      `Page ${page} of ${pageCount}   |   ${title}   |   ${dateLabel}`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 12,
      { align: "center" },
    );
  }

  return { doc, fileName: paymentReportFileName(startDate) };
}

// Build the PDF and hand it to the browser as a download.
export async function generatePaymentReportPdf(params) {
  const { doc, fileName } = await buildPaymentReportPdf(params);
  doc.save(fileName);
  return doc;
}
